/* ===================================================================
   Feedback -- native in-app feature-request board (HOPPER + CORBATO,
   pre-launch Step 2, 2026-07-14).

   Signed-in clients post feature requests and UPVOTE others; the most-
   wanted items float to the top by vote count (like Canny/Userjot, but
   native on our own Supabase + Supabase Auth -- no separate account).

   Data wiring (all LIVE):
     GET   /feedback-board/posts        -> useFeedbackBoard()  (sorted by votes)
     POST  /feedback-board/posts        -> useCreateFeedbackPost()
     POST  /feedback-board/posts/{id}/vote -> useToggleFeedbackVote() (one/client)
     PATCH /feedback-board/posts/{id}   -> useUpdateFeedbackPost()    (own post)

   Accessibility: big tap targets, bold high-contrast type, real field
   labels -- older-tradesman-on-a-glary-phone rule. No dev code shown.
   Status is roadmap state set by the Rhody Reviews team only; owners see
   it as a read-only chip.
   =================================================================== */
import { useMemo, useState } from 'react';
import { Card, EmptyState, Skeleton } from '../components/ui';
import {
  useFeedbackBoard,
  useCreateFeedbackPost,
  useToggleFeedbackVote,
  useUpdateFeedbackPost,
} from '../hooks';
import type { FeedbackPost } from '../lib/api';
import { relativeTime, apiErrorMessage } from '../lib/display';
import '../styles/feedback-board.css';

const MAX_TITLE = 140;

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  planned: 'Planned',
  in_progress: 'In progress',
  done: 'Done',
  declined: 'Declined',
};

function CaretUp() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
      <path d="M6 15l6-6 6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function VoteButton({
  post,
  pending,
  onClick,
}: {
  post: FeedbackPost;
  pending: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={'fb-vote' + (post.has_voted ? ' is-voted' : '')}
      onClick={onClick}
      disabled={pending}
      aria-pressed={post.has_voted}
      aria-label={
        (post.has_voted ? 'Remove your upvote from' : 'Upvote') +
        ` "${post.title}". ${post.vote_count} ` +
        (post.vote_count === 1 ? 'vote' : 'votes')
      }
    >
      <CaretUp />
      <span className="fb-vote-count">{post.vote_count}</span>
      <span className="fb-vote-lbl">{post.has_voted ? 'Voted' : 'Vote'}</span>
    </button>
  );
}

function PostRow({
  post,
  votePendingId,
  onVote,
}: {
  post: FeedbackPost;
  votePendingId: string | null;
  onVote: (id: string) => void;
}) {
  const updater = useUpdateFeedbackPost();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(post.title);
  const [body, setBody] = useState(post.body ?? '');

  const statusKey = String(post.status || 'open');
  const showStatus = statusKey !== 'open';

  function save() {
    const t = title.trim();
    if (!t) return;
    void updater
      .update(post.id, { title: t, body: body.trim() })
      .then(() => setEditing(false))
      .catch(() => {
        /* refetch restores the true row; error shown inline below */
      });
  }

  if (editing) {
    return (
      <Card className="fb-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <div className="fb-field">
          <label htmlFor={`edit-title-${post.id}`}>Title</label>
          <input
            id={`edit-title-${post.id}`}
            className="fb-input"
            value={title}
            maxLength={MAX_TITLE}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="fb-field">
          <label htmlFor={`edit-body-${post.id}`}>Details (optional)</label>
          <textarea
            id={`edit-body-${post.id}`}
            className="fb-textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        {updater.error && (
          <div className="fb-error" role="alert">
            {apiErrorMessage(updater.error)}
          </div>
        )}
        <div className="fb-compose-row">
          <button
            type="button"
            className="btn"
            onClick={() => {
              setTitle(post.title);
              setBody(post.body ?? '');
              setEditing(false);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={save}
            disabled={!title.trim() || updater.pendingId === post.id}
          >
            {updater.pendingId === post.id ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="fb-item">
      <VoteButton
        post={post}
        pending={votePendingId === post.id}
        onClick={() => onVote(post.id)}
      />
      <div className="fb-body">
        <div className="fb-title-row">
          <h3 className="fb-title">{post.title}</h3>
          {showStatus && (
            <span className={'fb-status ' + statusKey}>
              {STATUS_LABEL[statusKey] ?? statusKey}
            </span>
          )}
        </div>
        {post.body && <p className="fb-text">{post.body}</p>}
        <div className="fb-meta">
          <span>{relativeTime(post.created_at)}</span>
          {post.is_author && <span className="fb-you">Your request</span>}
          {post.is_author && (
            <button type="button" className="fb-editbtn" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

export function Feedback() {
  const q = useFeedbackBoard();
  const creator = useCreateFeedbackPost();
  const voter = useToggleFeedbackVote();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [filter, setFilter] = useState<string>('all');

  const posts = useMemo<FeedbackPost[]>(() => q.data?.posts ?? [], [q.data]);
  const statuses = useMemo<string[]>(() => q.data?.statuses ?? [], [q.data]);

  const visible = useMemo(
    () => (filter === 'all' ? posts : posts.filter((p) => p.status === filter)),
    [posts, filter],
  );

  function submit() {
    const t = title.trim();
    if (!t) return;
    void creator
      .create({ title: t, body: body.trim() })
      .then(() => {
        setTitle('');
        setBody('');
      })
      .catch(() => {
        /* error shown inline; refetch keeps the list honest */
      });
  }

  return (
    <div className="fb-wrap">
      <div className="fb-head">
        <h1>Feature requests</h1>
        <p>
          Tell us what would make Rhody Reviews better, and upvote the ideas you
          want most. The most-requested ideas rise to the top.
        </p>
      </div>

      {/* New request composer */}
      <Card className="fb-compose">
        <h2>Request a feature</h2>
        <div className="fb-field">
          <label htmlFor="fb-new-title">What would you like?</label>
          <input
            id="fb-new-title"
            className="fb-input"
            placeholder="Short summary, e.g. Text me when a new review lands"
            value={title}
            maxLength={MAX_TITLE}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="fb-field">
          <label htmlFor="fb-new-body">A little more detail (optional)</label>
          <textarea
            id="fb-new-body"
            className="fb-textarea"
            placeholder="What are you trying to do, and how would this help?"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        {creator.error && (
          <div className="fb-error" role="alert">
            {apiErrorMessage(creator.error)}
          </div>
        )}
        <div className="fb-compose-row">
          <span className="fb-count">
            {title.length}/{MAX_TITLE}
          </span>
          <button
            type="button"
            className="btn btn--primary"
            onClick={submit}
            disabled={!title.trim() || creator.isPending}
          >
            {creator.isPending ? 'Posting...' : 'Post request'}
          </button>
        </div>
      </Card>

      {/* Status filters */}
      {statuses.length > 0 && (
        <div className="fb-filters" role="group" aria-label="Filter by status">
          <button
            type="button"
            className={'fb-filter' + (filter === 'all' ? ' is-active' : '')}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          {statuses.map((s) => (
            <button
              key={s}
              type="button"
              className={'fb-filter' + (filter === s ? ' is-active' : '')}
              onClick={() => setFilter(s)}
            >
              {STATUS_LABEL[s] ?? s}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      {q.isLoading ? (
        <div className="fb-list fb-loading">
          <Skeleton style={{ height: 92 }} />
          <Skeleton style={{ height: 92 }} />
          <Skeleton style={{ height: 92 }} />
        </div>
      ) : visible.length === 0 ? (
        <div className="fb-empty">
          <EmptyState
            title={filter === 'all' ? 'No requests yet' : 'Nothing here yet'}
            desc={
              filter === 'all'
                ? 'Be the first to tell us what would help your business.'
                : 'No requests in this status yet. Try another filter.'
            }
          />
        </div>
      ) : (
        <div className="fb-list">
          {visible.map((p) => (
            <PostRow
              key={p.id}
              post={p}
              votePendingId={voter.pendingId}
              onVote={(id) => {
                void voter.toggle(id).catch(() => {
                  /* refetch reconciles the count on failure */
                });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
